"use client"

import { useState, useEffect, useMemo, useCallback } from "react"
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  SortingState,
  ColumnFiltersState,
} from "@tanstack/react-table"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { useToast } from "@/components/ui/use-toast"
import { supabase } from "@/utils/supabase/client"
import { useAuth } from "@/contexts/auth-context"
import { Payment, Patient } from "../types"
import { Loader2, MoreHorizontal } from "lucide-react"
import { format } from "date-fns"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

type PaymentWithPatient = Payment & {
  patients: Pick<Patient, 'name' | 'mr_number'>
}

interface PaymentHistoryProps {
  onEdit: (payment: Payment) => void;
  key: number;
}

export function PaymentHistory({ onEdit, key: refreshKey }: PaymentHistoryProps) {
  const [data, setData] = useState<PaymentWithPatient[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const { toast } = useToast()
  const { currentOrganization } = useAuth()

  const fetchPayments = useCallback(async () => {
    if (!currentOrganization) return
    setIsLoading(true)
    try {
      const filterValue = (columnFilters.find(f => f.id === 'patientName')?.value as string) ?? '';

      let query = supabase
        .from("payments")
        .select(
          `
          *,
          patients!inner(
            name,
            mr_number
          )
        `
        )
        .eq("organization_id", currentOrganization.id)
        .order("created_at", { ascending: false })

      if (filterValue) {
        query = query.ilike("patients.name", `%${filterValue}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      // @ts-ignore
      setData(data);
    } catch (error: any) {
      toast({
        title: "Error fetching payments",
        description: error.message,
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }, [currentOrganization, toast, columnFilters])

  useEffect(() => {
    fetchPayments()
  }, [fetchPayments, refreshKey])

  const handleDelete = useCallback(async (paymentId: string) => {
    if(!window.confirm("Are you sure you want to delete this payment record?")) return;

    const { error } = await supabase.from('payments').delete().eq('id', paymentId);
    if(error){
       toast({
        title: "Error deleting payment",
        description: error.message,
        variant: "destructive",
      })
    } else {
       toast({
        title: "Payment deleted",
        description: "The payment record has been successfully deleted.",
      })
      fetchPayments();
    }
  }, [fetchPayments, toast]);

  const columns: ColumnDef<PaymentWithPatient>[] = useMemo(() => [
    {
      id: "patientName",
      accessorFn: row => row.patients.name,
      header: "Patient",
       cell: ({ row }) => {
        const payment = row.original;
        return (
          <div>
            { /* @ts-ignore */ }
            <div className="font-medium">{payment.patients.name}</div>
            { /* @ts-ignore */ }
            <div className="text-sm text-muted-foreground">MR#: {payment.patients.mr_number}</div>
          </div>
        )
      }
    },
    {
      accessorKey: "amount_paid",
      header: "Amount Paid",
      cell: ({ row }) => `Rs. ${row.getValue("amount_paid")?.toLocaleString()}`,
    },
    {
      accessorKey: "payment_method",
      header: "Method",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.getValue("status") as string
        const variant = status === "Paid" ? "default" : status === "Pending" ? "secondary" : "destructive"
        return <Badge variant={variant as "default" | "secondary" | "destructive" | "outline" | null | undefined}>{status}</Badge>
      },
    },
    {
      accessorKey: "created_at",
      header: "Date",
      cell: ({ row }) => format(new Date(row.getValue("created_at")), "PPP"),
    },
     {
      id: "actions",
      cell: ({ row }) => {
        const payment = row.original

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onEdit(payment)}>
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleDelete(payment.id)} className="text-red-600">
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ], [onEdit, handleDelete])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: {
      sorting,
      columnFilters,
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <Card className="mt-6">
        <CardHeader>
            <CardTitle>Payment History</CardTitle>
            <CardDescription>View and manage all recorded payments.</CardDescription>
        </CardHeader>
        <CardContent>
            <div className="flex items-center py-4">
                <Input
                placeholder="Filter by patient name..."
                value={(table.getColumn("patientName")?.getFilterValue() as string) ?? ""}
                onChange={(event) =>
                    table.getColumn("patientName")?.setFilterValue(event.target.value)
                }
                className="max-w-sm"
                />
            </div>
             <div className="rounded-md border">
                <Table>
                <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                        return (
                            <TableHead key={header.id}>
                            {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                )}
                            </TableHead>
                        )
                        })}
                    </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                        <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && "selected"}
                        >
                        {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                        ))}
                        </TableRow>
                    ))
                    ) : (
                    <TableRow>
                        <TableCell colSpan={columns.length} className="h-24 text-center">
                        No results.
                        </TableCell>
                    </TableRow>
                    )}
                </TableBody>
                </Table>
            </div>
            <div className="flex items-center justify-end space-x-2 py-4">
                <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                >
                Previous
                </Button>
                <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                >
                Next
                </Button>
            </div>
        </CardContent>
    </Card>
  )
} 